import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import PageFaq from "../../components/content/PageFaq";
import ConversionCtas from "../../components/content/ConversionCtas";
import { buildProductJsonLd, buildFaqPageJsonLd } from "../../lib/seoJsonLd";
import { EDITORIAL, INGREDIENTS } from "../../lib/data";
import { INGREDIENT_IMAGE_FALLBACK } from "../../lib/ingredients";
import { PHOTO_BRIEF } from "../../lib/contentImagePlaceholders";

const purpleFaqs = [
  {
    question: "Why choose Purple Sea Moss?",
    answer:
      "Purple offers a deeper colour and a bolder natural profile on the spoon: a premium alternative to gold when you want variety. It is still sea moss and spring water; we describe taste and ritual, not medical outcomes.",
  },
  {
    question: "How is purple different from gold?",
    answer:
      "Gold tends to feel smooth and mild; purple reads richer and more botanical. Neither is “better for your health” in our copy. Rotate based on mood and taste.",
  },
  {
    question: "Which herbs pair well with purple?",
    answer:
      "Customers commonly layer elderberry, hibiscus, and ginger with purple for tart, floral, or warming notes. Always pantry-style combinations for taste, not as cures.",
  },
  {
    question: "Can I order 1 litre with herbs?",
    answer:
      "Yes. One litre glass jars support custom botanical blends with up to three powdered herbs, same as 500 ml. Our 300 ml jar and 250 ml foil pouch are plain sea moss gel only.",
  },
];

const pairingHerbs = ["elderberry", "hibiscus", "ginger"].map((id) => INGREDIENTS.find((i) => i.id === id)).filter(Boolean);

export default function PurpleSeaMossGelPage() {
  const path = "/products/purple-sea-moss-gel";
  const img = EDITORIAL.purpleComposition;
  const productLd = buildProductJsonLd({
    name: "Purple Sea Moss Gel",
    description:
      "Wildcrafted purple sea moss gel: deeper colour, bold natural profile, premium alternative to gold. Mineral-rich and commonly used in daily wellness routines.",
    urlPath: path,
    image: img,
    lowPrice: "14.99",
    highPrice: "39.99",
    offerCount: "6",
  });

  return (
    <SiteShell>
      <SEO
        title="Purple Sea Moss Gel | Deeper colour, bold profile"
        description="MossMatrix Purple Sea Moss Gel (UK): premium deeper-toned base, spring water, optional powdered botanicals on 500 ml and 1 litre jars. 300 ml jar plain. Priced above gold."
        path={path}
        ogType="product"
        jsonLd={[productLd, buildFaqPageJsonLd(purpleFaqs)]}
      />
      <ContentPageLayout
        eyebrow="Product"
        title="Purple Sea Moss Gel"
        subtitle="Purple Sea Moss is our deeper, more premium base: naturally darker in colour and priced higher due to sourcing and availability. Bold on the spoon, still honestly just sea moss and spring water. We describe taste and ritual, not medical outcomes."
      >
        <div className="grid lg:grid-cols-2 gap-10 items-start mb-14">
          <div>
            <div className="rounded-[2rem] overflow-hidden border border-cream aspect-[4/5] max-h-[480px]">
              <img src={img} alt="MossMatrix purple sea moss gel" className="w-full h-full object-cover editorial-img" />
            </div>
            <p className="mt-2 text-[10px] text-botanical/50 leading-relaxed">{PHOTO_BRIEF.jarPurpleSideBySide}</p>
          </div>
          <div className="space-y-6">
            <section className="rounded-2xl border border-cream bg-white p-6">
              <h2 className="font-serif text-xl text-forest">Why choose Purple Sea Moss?</h2>
              <p className="mt-3 text-sm text-botanical/85 leading-relaxed">
                Purple-hued sea moss naturally carries deeper colour pigments. Many customers enjoy that visual drama in drinks and bowls. We keep language grounded: it is a taste-led, botanical-forward base, not a miracle supplement.
              </p>
            </section>
            <section className="rounded-2xl border border-cream bg-cream/40 p-6 text-sm text-botanical/85 leading-relaxed">
              <h2 className="font-serif text-lg text-forest">Gold vs Purple</h2>
              <p className="mt-2">
                Gold is smooth and classic; purple leans bolder and more botanical. Read the full comparison on{" "}
                <Link to="/learn/gold-vs-purple-sea-moss" className="underline text-forest hover:text-gold">
                  our learn page
                </Link>
                .
              </p>
            </section>
            <div className="flex flex-wrap gap-3">
              <Link to="/#builder" className="inline-flex rounded-full bg-forest text-cream px-6 py-3 text-sm hover:bg-botanical transition">
                Build a Purple blend
              </Link>
              <Link to="/ingredients" className="rounded-full border border-forest/20 px-6 py-3 text-sm hover:bg-cream transition">
                Herb library
              </Link>
            </div>
          </div>
        </div>

        <section className="rounded-3xl border border-cream bg-white p-8 md:p-10 mb-10">
          <h2 className="font-serif text-2xl text-forest">Best pairings: elderberry, hibiscus, ginger</h2>
          <p className="mt-3 text-sm text-botanical/85 max-w-3xl">
            These pantry herbs are commonly combined with purple for tart, floral, or warming notes. We love them for taste and colour, not for treating or curing anything.
          </p>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {pairingHerbs.map((ing) => (
              <div key={ing.id} className="rounded-2xl border border-cream overflow-hidden bg-offwhite">
                <div className="aspect-[4/3]">
                  <img
                    src={ing.image || INGREDIENT_IMAGE_FALLBACK}
                    alt={ing.imageAlt || `${ing.name} ingredient image`}
                    className="w-full h-full object-cover editorial-img"
                  />
                </div>
                <div className="p-3 text-center font-serif text-forest">{ing.name}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-3xl border border-cream bg-offwhite p-8 md:p-10 mb-10">
          <h2 className="font-serif text-2xl text-forest">Available sizes</h2>
          <p className="mt-3 text-sm text-botanical/80 max-w-2xl">
            Purple is our premium base, priced above gold. Foil pouches are best value; glass jars are fridge-ready. Trial and 300 ml are plain only.
            On 500 ml and 1 litre you can stay plain or add up to three powdered botanicals at £1.50 each in the builder.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-botanical/85">
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">200 ml trial pouch</h3>
              <p className="mt-2">Plain £6.99</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">300 ml pouch</h3>
              <p className="mt-2">Plain £9.99</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">300 ml jar</h3>
              <p className="mt-2">Plain £11.99</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">500 ml pouch</h3>
              <p className="mt-2">Plain £15.99 · Custom from £17.49</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">500 ml jar</h3>
              <p className="mt-2">Plain £17.99 · Custom from £19.49</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">1 litre pouch</h3>
              <p className="mt-2">Plain £26.99 · Custom from £28.49</p>
            </div>
            <div className="rounded-xl bg-white border border-cream p-4">
              <h3 className="font-serif text-lg text-forest">1 litre jar</h3>
              <p className="mt-2">Plain £29.99 · Custom from £31.49</p>
            </div>
          </div>
        </section>

        <PageFaq items={purpleFaqs} />
        <ConversionCtas />

        <p className="text-center text-sm text-botanical/70 mt-10">
          <Link to="/shop" className="underline hover:text-forest">
            Back to shop
          </Link>
          {" · "}
          <Link to="/products/gold-sea-moss-gel" className="underline hover:text-forest">
            View gold gel
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-forest">
            FAQ
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
